// DashboardNotaireFront/src/api/chatApi.ts
import { apiClient, getAuthTokens } from '@/taskpane/utils/client';
import { ApiCitation } from '../modules/ai-chat/messages';



interface TextInferenceConfigValues {
    temperature?: number;
    topP?: number;
    maxTokens?: number;
    stopSequences?: string[];
}
  
  // Interface for the {"textInferenceConfig": {...}} structure
interface NestedInferenceConfig {
    textInferenceConfig?: TextInferenceConfigValues;
}
  
interface PromptTemplate {
    textPromptTemplate: string;
}
  
interface OrchestrationConfiguration {
    promptTemplate: PromptTemplate;
    inferenceConfig?: NestedInferenceConfig; // Updated
}
  
interface GenerationConfiguration {
    promptTemplate: PromptTemplate;
    inferenceConfig?: NestedInferenceConfig; // Updated
}
  // ... (VectorSearchConfiguration, RetrievalConfiguration, KnowledgeBaseConfigurationPayload remain the same structurally but will use these updated types)
  
export interface KnowledgeBaseConfigurationPayload {
    knowledgeBaseId: string;
    modelArn: string;
    retrievalConfiguration?: RetrievalConfiguration;
    orchestrationConfiguration?: OrchestrationConfiguration; // Uses updated type
    generationConfiguration?: GenerationConfiguration;   // Uses updated type
    [key: string]: any;
}

interface VectorSearchConfiguration {
  numberOfResults?: number;
  overrideSearchType?: 'HYBRID' | 'SEMANTIC';
  // filter is set by backend
}

interface RetrievalConfiguration {
  vectorSearchConfiguration?: VectorSearchConfiguration; // Optional at this level if backend sets defaults
}

export interface ChatAskBackendPayload {
    client_id?: string;
    dossier_id?: string;
    sous_dossier_id?: string;
    message: string;
    chat_session_id?: string;
}

// --- Response Payloads ---

export interface Citation {
  content: string;
  location: string;
}

export interface ChatBackendResponse {
  session_id: string;
  message: string;
  citations: ApiCitation[];
}

/**
 * Sends a question to the chat backend API.
 * @param payload The data for the chat question.
 * @param options Optional parameters, including an AbortSignal to cancel the request.
 * @returns The response from the backend.
 */
export const askChatQuestion = async (
    payload: ChatAskBackendPayload,
    options: { signal?: AbortSignal } = {}
): Promise<ChatBackendResponse> => {
    try {
        // Use the pre-configured apiClient. It handles the base URL and auth headers.
        const response = await apiClient.post<ChatBackendResponse>(`/chat`, payload, {
            signal: options?.signal,
        });
        return response.data;
    } catch (error: any) {
        // Re-throw AbortError/CanceledError so the calling component can handle it silently.
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
            throw error;
        }

        // For other errors, log and throw a generic error message.
        console.error('API Error in askChatQuestion:', error.response?.data || error.message);
        throw new Error('Failed to get response from the assistant.');
    }
}

export interface RagStatusPayload {
    dossier_id?: string;
    sous_dossier_id?: string;
}

export const getSousDossierRagStatus = async (
    payload: RagStatusPayload
): Promise<any> => {
    const response = await apiClient.get('/dossier/get-sous-dossier-rag-status', { params: payload });
    return response.data;
}

export interface IndexingStatusPayload {
    dossier_id: string;
    sous_dossier_id: string;
}

export interface IndexingStatusResponse {
    dossier_id: string;
    sous_dossier_id: string;
    all_documents_indexed: boolean;
    total_documents: number;
    indexed_documents: number;
    pending_documents: number;
    failed_documents: number;
    documents: Array<{
        input_id: string;
        input_original_filename: string;
        input_classifier_rename: string;
        step_kb_index_status: string;
        step_kb_index_errors: string[];
    }>;
}

export const getSousDossierIndexingStatus = async (
    payload: IndexingStatusPayload
): Promise<IndexingStatusResponse> => {
    const response = await apiClient.get(
        `/chat/dossier/${payload.dossier_id}/sous-dossier/${payload.sous_dossier_id}/indexing-status`
    );
    return response.data;
}

export interface LogErrorPayload {
    client_id: string;
    dossier_id?: string;
    sous_dossier_id?: string;
    chat_session_id?: string;
    error_details: {
        message: string;
        stack?: string;
        context: string;
    };
}

export const logChatError = async (payload: LogErrorPayload): Promise<any> => {
    try {
        await apiClient.post(`/chat/${payload.chat_session_id}/log`, { action: 'log_error', ...payload });
    } catch (logError) {
        console.error("Failed to log error to backend:", logError);
    }
 };

export const voteMessage = async (payload: {
  chat_session_id: string;
  message_id: string;
  vote_status: 'good' | 'bad';
}): Promise<void> => {
  const response = await apiClient.patch(`/chat/${payload.chat_session_id}`, {
    message_id: payload.message_id,
    vote_status: payload.vote_status,
  });
  return response.data;
};

export const updateSousDossierRagStatus = async (payload: {
  dossier_id: string;
  sous_dossier_id: string;
  index_request: string;
}): Promise<void> => {
  try {
    const response = await apiClient.put(`/dossier/${payload.dossier_id}/sous-dossier/${payload.sous_dossier_id}/update-rag-index-status`, payload);
    if (response.status !== 200) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.data;
  } catch (error) {
    console.error('Error updating sous-dossier RAG status:', error);
    throw error;
  }
};

// --- Chat Sessions History ---

export interface ChatSessionSummary {
  session_id: string;
  first_question: string;
  first_answer: string;
  created_on: string;
  message_count: number;
}

export interface ChatSessionsListResponse {
  sessions: ChatSessionSummary[];
  next_cursor: string | null;
  total_count: number;
}

export interface ChatSessionsListPayload {
  dossier_id: string;
  sous_dossier_id: string;
  limit?: number;
  cursor?: string;
}

/**
 * Fetches a paginated list of chat sessions for a sous-dossier.
 * @param payload The dossier_id, sous_dossier_id, and optional pagination params
 * @returns A list of session summaries with pagination info
 */
export const getChatSessionsList = async (
  payload: ChatSessionsListPayload
): Promise<ChatSessionsListResponse> => {
  const { dossier_id, sous_dossier_id, limit, cursor } = payload;

  const params: Record<string, string | number> = {};
  if (limit) params.limit = limit;
  if (cursor) params.cursor = cursor;

  try {
    const response = await apiClient.get<ChatSessionsListResponse>(
      `/chat/dossier/${dossier_id}/sous-dossier/${sous_dossier_id}/sessions`,
      { params }
    );
    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'name' in error) {
      const err = error as { name?: string };
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        throw error;
      }
    }
    console.error('API Error in getChatSessionsList:', error);
    throw new Error("Impossible de charger l'historique des conversations.");
  }
};

export interface DeleteChatSessionResponse {
  message: string;
  session_id: string;
}

/**
 * Deletes a chat session by session_id.
 * @param sessionId The UUID of the session to delete
 * @returns Confirmation of deletion
 */
export const deleteChatSession = async (sessionId: string): Promise<DeleteChatSessionResponse> => {
  try {
    const response = await apiClient.delete<DeleteChatSessionResponse>(`/chat/${sessionId}`);
    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'name' in error) {
      const err = error as { name?: string };
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        throw error;
      }
    }
    console.error('API Error in deleteChatSession:', error);
    throw new Error('Impossible de supprimer la conversation.');
  }
};